import React, { useContext, useEffect, useRef, useState } from 'react';
import { SearchModes } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import type { SearchParams } from '@/server/searchParams.ts';
import { isOpxDeal } from '@/app/components/search/components/Card/utils/getPromotionsUtils.ts';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import TaxesIncludedTooltip from '@/app/components/search/components/EvolutionCard/Details/Price/TaxesIncludedTooltip';
import TaxesPopover from './TaxesPopover';
import { TaxesAnchor } from './styled';
import type { DealCard } from '../../../../../../utils/buildDealCards/dealCardTypes';

type Props = {
  isOverlay: boolean;
  taxesText: string | null;
  dealCard: DealCard;
  showTotalPrice: boolean;
  showDoublePrice: boolean;
  travellersCount: number;
  searchParams: SearchParams;
};

const Taxes = ({ isOverlay, taxesText, dealCard, ...rest }: Props) => {
  const { searchInfoData, localization } = usePageWrapperData();
  const { isDesktop } = useCommonClient();
  const taxesIncludedDoublePricing = localization?.configValuesApp?.taxesIncludedDoublePricing;
  const taxesIncludedPopup = localization?.configValuesApp?.taxesIncludedPopup;
  const isDoublePricing = searchInfoData?.searchMode === SearchModes.DP && taxesIncludedDoublePricing;

  const { showTaxesIncludedTooltip, dismissTaxesIncludedTooltip, taxesIncludedTooltipClaimedRef } =
    useContext(SearchPageContext);
  const isTooltipOwnerRef = useRef(false);
  const [isTooltipOwner, setIsTooltipOwner] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      showTaxesIncludedTooltip &&
      taxesIncludedPopup &&
      isDesktop &&
      !isOverlay &&
      !isOpxDeal(dealCard) &&
      !taxesIncludedTooltipClaimedRef.current
    ) {
      taxesIncludedTooltipClaimedRef.current = true;
      isTooltipOwnerRef.current = true;
      setIsTooltipOwner(true);
    } else if (!showTaxesIncludedTooltip && isTooltipOwnerRef.current) {
      isTooltipOwnerRef.current = false;
      setIsTooltipOwner(false);
    }
  }, [showTaxesIncludedTooltip, taxesIncludedPopup, isDesktop, isOverlay, dealCard, taxesIncludedTooltipClaimedRef]);

  // The tooltip uses position:absolute and overflows below the card boundary.
  // Each CardContainer creates a stacking context with z-index:1, so the next card
  // (same z-index, later in DOM order) would paint on top of the overflow.
  // Elevating the owning card to z-index:2 makes its stacking context win over siblings.
  useEffect(() => {
    if (isTooltipOwner) {
      const card = anchorRef.current?.closest<HTMLElement>('[data-testid="card-container"]');
      if (card) card.style.zIndex = '2';
    }
    return () => {
      const c = anchorRef.current?.closest<HTMLElement>('[data-testid="card-container"]');
      if (c) c.style.zIndex = '';
    };
  }, [isTooltipOwner]);

  if (!taxesText && !isDoublePricing) return null;
  if (isOverlay) return taxesText ? <span>{taxesText}</span> : null;
  return (
    <TaxesAnchor ref={anchorRef}>
      <TaxesPopover taxesText={taxesText} dealCard={dealCard} {...rest} />
      {isTooltipOwner && <TaxesIncludedTooltip onDismiss={dismissTaxesIncludedTooltip} />}
    </TaxesAnchor>
  );
};

export default Taxes;
